// The in-memory twin of the native calendar (CALENDAR-C1; packages/schema/src/contracts/api/
// calendar.ts, docs/design/2026-10-28-calendar.md §2.2, §2.3, §4.1). Simplified but contract-true:
//
// - Calendars: the company's (named after it), one per team, one per person (the caller's made on
//   its first `calendars.list`, another person's on a follow by membership). Access (§2.2): the
//   company's is write for managers, read otherwise; a team's is write for whoever holds a seat in
//   it, read for managers, busy for the rest; a person's is write for its owner, busy (or read,
//   by its visibility) for everyone else. Live shares raise access. A client viewer reads the
//   company calendar only.
// - Follows: the company, the caller's teams and its own calendar are default follows (hidden,
//   never unfollowed: 409); others are follow rows.
// - Events: masters carry an RRULE (DAILY / WEEKLY with BYDAY / MONTHLY, INTERVAL, COUNT,
//   UNTIL, expanded here in UTC); exceptions are rows with `recurrence_parent_id` and
//   `original_start_at`; a deletion is a cancelled row. Busy blocks hide title, location,
//   organizer and attendees; `hidden` events are left out for anyone who cannot write.
// - Tasks overlay: the caller's tasks (assigned to a seat it holds) due in the window, read from
//   the Work twin.
// - ICS feed: one link per person; the token in the path is the credential.
//
// Seed (Acme only, every time relative to `now()`; M = Monday 00:00 UTC of now's week): see
// `seedAcme` below.
import {
  CalendarChangeCreate,
  CalendarEventCreate,
  CalendarEventPatch,
  CalendarFollowCreate,
  CalendarFollowPatch,
  CalendarPatch,
  CalendarPublish,
  CalendarRsvp,
  CalendarShareCreate,
  CalendarTaskQuery,
  CalendarWindowQuery,
} from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  peekJson,
  problem,
  validation,
} from "./fake";
import type { CadenceTeam } from "./fake-cadence";
import { COMPANY_IDS, type FakeCompany, uid } from "./fake-data";

type CalendarView = OperationOutput<"calendars.update">;
type CalRow = Omit<
  CalendarView,
  | "team_name"
  | "owner_name"
  | "access"
  | "manages"
  | "followed"
  | "default_follow"
  | "shown"
  | "display_color"
  | "board_id"
  | "board_name"
>;
type Item = OperationOutput<"calendarItems.list">["data"][number];
type Detail = OperationOutput<"calendarEvents.get">;
type EventRow = Omit<
  Detail,
  | "calendar_name"
  | "calendar_kind"
  | "attendees"
  | "organizer_name"
  | "series_rrule"
  | "series_id"
  | "series_version"
  | "occurrence_start"
  | "my_response"
  | "can_edit"
  | "can_rsvp"
  | "read_only_reason"
  | "revisions"
>;
type AttendeeView = Detail["attendees"][number];
type Attendee = Omit<AttendeeView, "name" | "member_kind">;
type ShareView = OperationOutput<"calendarShares.create">;
type Share = Omit<ShareView, "grantee_name">;
type Revision = OperationOutput<"calendarEvents.revisions">["data"][number];
type Occurrence = OperationOutput<"calendarEvents.list">["data"][number];
type Chip = OperationOutput<"calendarTasks.list">["data"][number];
type Access = CalendarView["access"];
type Response3 = Attendee["response"];
type Follow = {
  membership_id: string;
  calendar_id: string;
  shown: boolean;
  color_override: string | null;
};
type Feed = { token: string; created_at: string; last_used_at: string | null };
type Mode = "full" | "busy" | "none";

type Store = {
  company: FakeCompany;
  calendars: CalRow[];
  events: EventRow[];
  attendees: Attendee[];
  shares: Share[];
  follows: Follow[];
  revisions: Revision[];
  /** membership id → its live feed link. */
  feeds: Map<string, Feed>;
};

/** A Work task as the overlay reads it (the Work twin's StoredTask is one). */
export type CalendarWorkTask = {
  id: string;
  title: string;
  due_at: string | null;
  board_id: string;
  completed_at: string | null;
  assignee_seat_ids: string[];
  /** CAL-WORK-LAYERS: what a work calendar's chip reads (optional for older callers). */
  start_at?: string | null;
  status?: string;
  version?: number;
  restricted?: boolean;
  deleted?: boolean;
  fields?: Record<string, unknown>;
};

/** CAL-WORK-LAYERS: a board as a work calendar reads it. */
export type CalendarWorkBoard = {
  id: string;
  name: string;
  space_id?: string;
  archived_at?: string | null;
  status_set?: { key: string; name: string; done: boolean; color?: string | null | undefined }[];
};
export type CalendarWorkView = {
  id: string;
  board_id: string;
  kind: string;
  name: string;
  date_field: string | null;
};
export type CalendarWorkSpace = {
  id: string;
  kind: string;
  is_private: boolean;
  owner_membership_id: string | null;
  settings?: unknown;
};
/** CAL-WORK-LAYERS: a goal as the Milestones calendar reads it (the org twin's Goal is one). */
export type CalendarGoal = {
  id: string;
  kind: string;
  seat_id: string;
  title: string;
  due_at: string | null;
  period_key: string | null;
  metric_def_id: string | null;
  target: string | number | null;
  state: string;
  milestones: { id: string; title: string; due_at: string | null; done_at: string | null }[];
};

export type CalendarFakeDeps = {
  now: () => Date;
  teamsOf: (c: FakeCompany) => CadenceTeam[];
  teamMembersOf: (c: FakeCompany) => { team_id: string; seat_id: string }[];
  assignmentsOf: (
    c: FakeCompany,
  ) => { seat_id: string; membership_id: string; active_to: string | null }[];
  nameOf: (c: FakeCompany, membershipId: string) => string;
  /** `digital_employee` for a DE's membership, else `person`. */
  memberKindOf: (c: FakeCompany, membershipId: string) => "person" | "digital_employee";
  /** The demo accounts the Acme seed is written around (null outside Acme). */
  seedPeople: (companyId: string) => { owner: string | null; member: string | null };
  /** The Work twin's tasks and boards of a company (null before it is up). */
  work?: (companyId: string) => {
    tasks: CalendarWorkTask[];
    boards: CalendarWorkBoard[];
    views?: CalendarWorkView[];
    spaces?: CalendarWorkSpace[];
  } | null;
  /** CAL-WORK-LAYERS: the org twin's goals and the seats' titles (the Milestones layer). */
  goalsOf?: (c: FakeCompany) => CalendarGoal[];
  seatTitleOf?: (c: FakeCompany, seatId: string) => string | null;
  /** The boards a person starred (the overlay adds their tasks, as the API does). */
  starredBoardsOf?: (p: Principal, membershipId: string) => string[];
};

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const RANK: Record<Access, number> = { busy: 0, read: 1, write: 2 };
const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 4096;
const iso = (ms: number) => new Date(ms).toISOString();
const ms = (s: string) => Date.parse(s);
const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
const noContent = () => new Response(null, { status: 204 });
const firstIssue = (e: { issues: { path: PropertyKey[]; message: string }[] }) => {
  const i = e.issues[0];
  return i ? `${i.path.map(String).join(".") || "body"}: ${i.message}` : "invalid";
};

/** Monday 00:00 UTC of the week holding `d`. */
export function mondayOf(d: Date): number {
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return day - ((new Date(day).getUTCDay() + 6) % 7) * DAY;
}

// Recurrence ------------------------------------------------------------------------------------

type Rule = {
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  interval: number;
  /** Monday-based weekdays (0 = MO). */
  byday: number[];
  count: number | null;
  until: number | null;
};
const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

function parseUntil(v: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(v);
  if (!m) return null;
  const [y, mo, d, h, mi, s] = m.slice(1).map((x) => (x === undefined ? null : Number(x)));
  if (y == null || mo == null || d == null) return null;
  if (h == null) return Date.UTC(y, mo - 1, d) + DAY - 1000;
  return Date.UTC(y, mo - 1, d, h, mi ?? 0, s ?? 0);
}
const untilText = (t: number) =>
  iso(t)
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");

export function parseRule(rrule: string): Rule | null {
  const parts = new Map(
    rrule
      .replace(/^RRULE:/i, "")
      .split(";")
      .map((p) => p.split("=") as [string, string])
      .map(([k, v]) => [k.toUpperCase(), (v ?? "").toUpperCase()]),
  );
  const freq = parts.get("FREQ");
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY") return null;
  const byday = (parts.get("BYDAY") ?? "")
    .split(",")
    .map((d) => DAYS.indexOf(d.slice(-2)))
    .filter((d) => d >= 0)
    .sort((a, b) => a - b);
  const count = parts.has("COUNT") ? Number(parts.get("COUNT")) : null;
  return {
    freq,
    interval: Math.max(1, Number(parts.get("INTERVAL") ?? 1) || 1),
    byday,
    count: count && count > 0 ? count : null,
    until: parts.has("UNTIL") ? parseUntil(parts.get("UNTIL") ?? "") : null,
  };
}

/** Every slot of a series in order, from its first, bounded by COUNT / UNTIL / series_until. */
function* slotsOf(start: number, rule: Rule, seriesUntil: number | null): Generator<number> {
  const until = Math.min(rule.until ?? Infinity, seriesUntil ?? Infinity);
  let n = 0;
  const emit = (c: number) => {
    if (c > until) return "stop";
    n++;
    if (rule.count !== null && n > rule.count) return "stop";
    return "ok";
  };
  for (let k = 0; k < 20_000; k++) {
    const batch: number[] = [];
    if (rule.freq === "DAILY") batch.push(start + k * rule.interval * DAY);
    else if (rule.freq === "WEEKLY") {
      const dow = (new Date(start).getUTCDay() + 6) % 7;
      const base = start - dow * DAY;
      const days = rule.byday.length ? rule.byday : [dow];
      for (const d of days) {
        const c = base + (k * rule.interval * 7 + d) * DAY;
        if (c >= start) batch.push(c);
      }
    } else {
      const s = new Date(start);
      const c = Date.UTC(
        s.getUTCFullYear(),
        s.getUTCMonth() + k * rule.interval,
        s.getUTCDate(),
        s.getUTCHours(),
        s.getUTCMinutes(),
        s.getUTCSeconds(),
      );
      if (new Date(c).getUTCDate() === s.getUTCDate()) batch.push(c);
    }
    for (const c of batch) {
      if (emit(c) === "stop") return;
      yield c;
    }
  }
}

/** The rule with `UNTIL` set (and `COUNT` dropped, or set to `count` when given). */
function ruleWith(rrule: string, until: number | null, count: number | null): string {
  const kept = rrule
    .replace(/^RRULE:/i, "")
    .split(";")
    .filter((p) => !/^(UNTIL|COUNT)=/i.test(p));
  if (until !== null) kept.push(`UNTIL=${untilText(until)}`);
  if (count !== null) kept.push(`COUNT=${count}`);
  return kept.join(";");
}

// The twin --------------------------------------------------------------------------------------

export function createCalendarFake(d: CalendarFakeDeps): Partial<Record<RouteId, Handler>> {
  const stores = new Map<string, Store>();
  let seq = 0;
  const stamp = () => d.now().toISOString();
  const newId = (s: Store, kind: number) =>
    uid(0xcb00 + kind, companyNo(s.company.id) * 0x100000 + ++seq);
  const zoneOf = (c: FakeCompany) =>
    (c.calendar_config as { timezone?: string } | null | undefined)?.timezone ?? "UTC";
  const weekStartOf = (c: FakeCompany) =>
    (c.calendar_config as { weekStartDay?: number } | null | undefined)?.weekStartDay ?? 1;

  // Standing ------------------------------------------------------------------------------------

  const meOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id) ?? null;
  const meIdOf = (p: Principal) => meOf(p)?.membership_id ?? null;
  const isManager = (p: Principal) =>
    p.manager || p.roles.includes("owner") || p.roles.includes("admin");
  const clientOnly = (p: Principal) =>
    p.roles.length > 0 && p.roles.every((r) => r === "client_viewer");
  function heldBy(c: FakeCompany, membershipId: string | null): Set<string> {
    if (!membershipId) return new Set();
    return new Set(
      d
        .assignmentsOf(c)
        .filter((a) => a.active_to === null && a.membership_id === membershipId)
        .map((a) => a.seat_id),
    );
  }
  function teamsHeld(c: FakeCompany, membershipId: string | null): Set<string> {
    const held = heldBy(c, membershipId);
    const out = new Set<string>();
    if (!held.size) return out;
    for (const t of d.teamsOf(c))
      if (
        (t.lead_seat_id && held.has(t.lead_seat_id)) ||
        (t.human_owner_seat_id && held.has(t.human_owner_seat_id))
      )
        out.add(t.id);
    for (const m of d.teamMembersOf(c)) if (held.has(m.seat_id)) out.add(m.team_id);
    return out;
  }
  const teamOf = (c: FakeCompany, id: string | null) =>
    id ? (d.teamsOf(c).find((t) => t.id === id) ?? null) : null;

  // Stores --------------------------------------------------------------------------------------

  function calendarRow(
    s: Store,
    kind: CalRow["kind"],
    name: string,
    extra: Partial<CalRow> = {},
  ): CalRow {
    const t = stamp();
    const row: CalRow = {
      company_id: s.company.id,
      id: newId(s, 0),
      kind,
      team_id: null,
      owner_membership_id: null,
      view_id: null,
      name,
      color: null,
      visibility: kind === "person" ? "busy" : kind === "team" ? "busy" : "details",
      timezone: null,
      archived_at: null,
      actor_kind: "person",
      actor_membership_id: null,
      created_at: t,
      updated_at: t,
      ...extra,
    };
    s.calendars.push(row);
    return row;
  }
  function ensureCalendars(s: Store) {
    if (!s.calendars.some((c) => c.kind === "company"))
      calendarRow(s, "company", s.company.name, { color: "sky" });
    // CAL-WORK-LAYERS: the company's Milestones layer (read-only, everyone's).
    if (!s.calendars.some((c) => c.kind === "milestones"))
      calendarRow(s, "milestones", "Milestones", { color: "violet" });
    // Colours are the web's palette keys (the tag tokens), never literals.
    for (const team of d.teamsOf(s.company))
      if (!s.calendars.some((c) => c.kind === "team" && c.team_id === team.id))
        calendarRow(s, "team", team.name, { team_id: team.id, color: "emerald" });
  }
  function personCal(s: Store, membershipId: string): CalRow {
    return (
      s.calendars.find((c) => c.kind === "person" && c.owner_membership_id === membershipId) ??
      calendarRow(s, "person", d.nameOf(s.company, membershipId), {
        owner_membership_id: membershipId,
        // No colour: the web picks one per calendar from the palette.
        color: null,
        actor_membership_id: membershipId,
      })
    );
  }
  function storeOf(c: FakeCompany): Store {
    let s = stores.get(c.id);
    if (!s) {
      s = {
        company: c,
        calendars: [],
        events: [],
        attendees: [],
        shares: [],
        follows: [],
        revisions: [],
        feeds: new Map(),
      };
      stores.set(c.id, s);
      ensureCalendars(s);
      if (c.id === COMPANY_IDS.acme) seedAcme(s);
    } else ensureCalendars(s);
    return s;
  }
  const calOf = (s: Store, id: string) => s.calendars.find((c) => c.id === id) ?? null;
  const eventOf = (s: Store, id: string) => s.events.find((e) => e.id === id) ?? null;
  const attendeesOf = (s: Store, eventId: string) =>
    s.attendees.filter((a) => a.event_id === eventId);
  const followOf = (s: Store, me: string | null, calendarId: string) =>
    me ? s.follows.find((f) => f.membership_id === me && f.calendar_id === calendarId) : undefined;

  // Access --------------------------------------------------------------------------------------

  function shareLevel(p: Principal, s: Store, cal: CalRow): Access | null {
    const me = meIdOf(p);
    const teams = teamsHeld(s.company, me);
    const held = heldBy(s.company, me);
    let best: Access | null = null;
    for (const sh of s.shares) {
      if (sh.calendar_id !== cal.id || sh.revoked_at) continue;
      const mine =
        sh.grantee_kind === "company" ||
        (sh.grantee_kind === "membership" && sh.grantee_id === me) ||
        (sh.grantee_kind === "team" && teams.has(sh.grantee_id)) ||
        (sh.grantee_kind === "seat" && held.has(sh.grantee_id));
      if (mine && (best === null || RANK[sh.level] > RANK[best])) best = sh.level;
    }
    return best;
  }
  const raise = (a: Access, b: Access | null): Access => (b && RANK[b] > RANK[a] ? b : a);

  // CAL-WORK-LAYERS: a work calendar's board, view and space (null when any is gone).
  function workOf(s: Store, cal: CalRow) {
    if (cal.kind !== "work" || !cal.view_id) return null;
    const w = d.work?.(s.company.id) ?? null;
    const v = w?.views?.find((x) => x.id === cal.view_id) ?? null;
    const board = v ? (w?.boards.find((b) => b.id === v.board_id && !b.archived_at) ?? null) : null;
    if (!w || !v || !board) return null;
    const space = w.spaces?.find((x) => x.id === board.space_id) ?? null;
    return { w, view: v, board, space };
  }
  /** The work twin's rule, simplified: a public space's board is every member's; managers all. */
  function seesBoard(p: Principal, space: CalendarWorkSpace | null): boolean {
    if (clientOnly(p)) return false;
    if (!space) return isManager(p);
    if (space.kind === "personal") return space.owner_membership_id === meIdOf(p);
    return isManager(p) || !space.is_private;
  }
  function editsBoard(p: Principal, space: CalendarWorkSpace | null): boolean {
    if (!seesBoard(p, space)) return false;
    if (isManager(p) || space?.kind === "personal") return true;
    return (space?.settings as { member_access?: string } | undefined)?.member_access === "editor";
  }
  /** A work calendar's audience: the board's viewers and its share list. */
  function inAudience(p: Principal, s: Store, cal: CalRow): boolean {
    const wk = workOf(s, cal);
    if (!wk || cal.archived_at || clientOnly(p)) return false;
    return seesBoard(p, wk.space) || shareLevel(p, s, cal) !== null;
  }
  const isAgent = (p: Principal) => meOf(p)?.kind === "digital_employee";

  function accessOf(p: Principal, s: Store, cal: CalRow): Access {
    if (cal.kind === "work" || cal.kind === "milestones") return "read";
    if (clientOnly(p)) return cal.kind === "company" ? "read" : "busy";
    const me = meIdOf(p);
    const shared = shareLevel(p, s, cal);
    if (cal.kind === "company") return raise(isManager(p) ? "write" : "read", shared);
    if (cal.kind === "team") {
      if (cal.team_id && teamsHeld(s.company, me).has(cal.team_id)) return "write";
      return raise(isManager(p) ? "read" : "busy", shared);
    }
    if (me && cal.owner_membership_id === me) return "write";
    return raise(cal.visibility === "details" ? "read" : "busy", shared);
  }
  function managesOf(p: Principal, s: Store, cal: CalRow): boolean {
    if (clientOnly(p)) return false;
    const me = meIdOf(p);
    if (cal.kind === "milestones") return false;
    if (cal.kind === "work") return !isAgent(p) && (isManager(p) || cal.owner_membership_id === me);
    if (cal.kind === "company") return isManager(p);
    if (cal.kind === "team") {
      const lead = teamOf(s.company, cal.team_id)?.lead_seat_id;
      return isManager(p) || (!!lead && heldBy(s.company, me).has(lead));
    }
    return !!me && cal.owner_membership_id === me;
  }
  function isDefault(p: Principal, s: Store, cal: CalRow): boolean {
    const me = meIdOf(p);
    if (cal.kind === "company") return true;
    if (clientOnly(p)) return false;
    if (cal.kind === "milestones") return true;
    if (cal.kind === "work") return inAudience(p, s, cal);
    if (cal.kind === "team") return !!cal.team_id && teamsHeld(s.company, me).has(cal.team_id);
    return !!me && cal.owner_membership_id === me;
  }
  function view(p: Principal, s: Store, cal: CalRow): CalendarView {
    const me = meIdOf(p);
    const f = followOf(s, me, cal.id);
    const def = isDefault(p, s, cal);
    const wk = workOf(s, cal);
    return {
      ...cal,
      board_id: wk?.board.id ?? null,
      board_name: wk?.board.name ?? null,
      team_name: cal.kind === "team" ? (teamOf(s.company, cal.team_id)?.name ?? cal.name) : null,
      owner_name:
        cal.kind === "person" && cal.owner_membership_id
          ? d.nameOf(s.company, cal.owner_membership_id)
          : null,
      access: accessOf(p, s, cal),
      manages: managesOf(p, s, cal),
      followed: def || !!f,
      default_follow: def,
      shown: f?.shown ?? true,
      display_color: f?.color_override ?? cal.color ?? null,
    };
  }
  /** The calendars in the caller's overlay: its defaults and its follows. */
  function listed(p: Principal, s: Store): CalRow[] {
    const me = meIdOf(p);
    return s.calendars.filter(
      (cal) =>
        !cal.archived_at &&
        (cal.kind !== "work" || inAudience(p, s, cal)) &&
        (clientOnly(p)
          ? cal.kind === "company"
          : isDefault(p, s, cal) || !!followOf(s, me, cal.id)),
    );
  }

  /** What the caller sees of one event row (the row's own visibility, its calendar, attendance). */
  function modeOf(p: Principal, s: Store, ev: EventRow): Mode {
    const cal = calOf(s, ev.calendar_id);
    if (!cal) return "none";
    if (clientOnly(p) && cal.kind !== "company") return "none";
    const acc = accessOf(p, s, cal);
    if (acc === "write") return "full";
    const me = meIdOf(p);
    if (me && ev.organizer_membership_id === me) return "full";
    const attends = !!me && attendeesOf(s, ev.id).some((a) => a.membership_id === me);
    if (attends) return ev.visibility === "busy" ? "busy" : "full";
    if (ev.visibility === "hidden") return "none";
    if (acc === "read") return ev.visibility === "busy" ? "busy" : "full";
    if (ev.visibility === "details") return "full";
    if (cal.visibility === "hidden" && ev.visibility === "default") return "none";
    return "busy";
  }
  function canEdit(p: Principal, s: Store, ev: EventRow): boolean {
    if (ev.source === "meeting") return false;
    const cal = calOf(s, ev.calendar_id);
    if (!cal) return false;
    const me = meIdOf(p);
    return accessOf(p, s, cal) === "write" || (!!me && ev.organizer_membership_id === me);
  }
  const ownerNameOf = (s: Store, cal: CalRow) =>
    cal.kind === "person" && cal.owner_membership_id
      ? d.nameOf(s.company, cal.owner_membership_id)
      : cal.kind === "team"
        ? (teamOf(s.company, cal.team_id)?.name ?? cal.name)
        : cal.name;

  // Rows ----------------------------------------------------------------------------------------

  function newEvent(s: Store, p: Principal | null, fields: Partial<EventRow>): EventRow {
    const t = stamp();
    const row: EventRow = {
      company_id: s.company.id,
      id: newId(s, 1),
      calendar_id: "",
      title: "",
      description: null,
      location: null,
      starts_at: t,
      ends_at: t,
      all_day: false,
      timezone: zoneOf(s.company),
      status: "confirmed",
      visibility: "default",
      organizer_membership_id: p ? meIdOf(p) : null,
      rrule: null,
      series_until: null,
      recurrence_parent_id: null,
      original_start_at: null,
      source: "manual",
      source_ref: null,
      version: 1,
      last_external_sync_at: null,
      cancelled_at: null,
      actor_kind: "person",
      actor_membership_id: p ? meIdOf(p) : null,
      created_at: t,
      updated_at: t,
      ...fields,
    };
    s.events.push(row);
    return row;
  }
  function addAttendee(
    s: Store,
    eventId: string,
    who: { membership_id?: string; email?: string; optional?: boolean | undefined },
    response: Response3 = "needs_action",
  ) {
    const t = stamp();
    s.attendees.push({
      company_id: s.company.id,
      id: newId(s, 2),
      event_id: eventId,
      membership_id: who.membership_id ?? null,
      email: who.email ?? null,
      response,
      optional: who.optional ?? false,
      responded_at: response === "needs_action" ? null : t,
      actor_kind: "person",
      actor_membership_id: null,
      created_at: t,
      updated_at: t,
    });
  }
  function setAttendees(
    s: Store,
    eventId: string,
    input: (
      | { membership_id: string; optional?: boolean | undefined }
      | { email: string; optional?: boolean | undefined }
    )[],
  ) {
    const before = attendeesOf(s, eventId);
    s.attendees = s.attendees.filter((a) => a.event_id !== eventId);
    for (const a of input) {
      const mid = "membership_id" in a ? a.membership_id : undefined;
      const email = "email" in a ? a.email : undefined;
      const prev = before.find((x) =>
        mid ? x.membership_id === mid : x.email !== null && x.email === email,
      );
      if (prev) s.attendees.push({ ...prev, optional: a.optional ?? prev.optional });
      else
        addAttendee(s, eventId, {
          ...(mid ? { membership_id: mid } : {}),
          ...(email ? { email } : {}),
          optional: a.optional,
        });
    }
  }
  function copyAttendees(s: Store, fromId: string, toId: string) {
    for (const a of attendeesOf(s, fromId))
      s.attendees.push({ ...a, id: newId(s, 2), event_id: toId });
  }
  function revise(s: Store, p: Principal, row: EventRow) {
    s.revisions.push({
      company_id: s.company.id,
      id: newId(s, 5),
      event_id: row.id,
      version: row.version,
      snapshot: { ...row },
      reason: "edit",
      actor_kind: "person",
      actor_membership_id: meIdOf(p),
      created_at: stamp(),
    });
  }
  const bump = (row: EventRow) => {
    row.version += 1;
    row.updated_at = stamp();
  };
  const exceptionsOf = (s: Store, masterId: string) =>
    s.events.filter((e) => e.recurrence_parent_id === masterId);
  function exceptionAt(s: Store, masterId: string, slot: number) {
    return exceptionsOf(s, masterId).find(
      (e) => e.original_start_at !== null && ms(e.original_start_at) === slot,
    );
  }
  function newException(s: Store, p: Principal, master: EventRow, slot: number): EventRow {
    const dur = ms(master.ends_at) - ms(master.starts_at);
    const { id: _id, ...rest } = master;
    const t = stamp();
    const row = newEvent(s, p, {
      ...rest,
      rrule: null,
      series_until: null,
      recurrence_parent_id: master.id,
      original_start_at: iso(slot),
      starts_at: iso(slot),
      ends_at: iso(slot + dur),
      version: 1,
      created_at: t,
      updated_at: t,
    });
    copyAttendees(s, master.id, row.id);
    return row;
  }

  /**
   * The row whose version guards a change (the API's `guardRow`): the master for "following" and
   * "all", the occurrence for "this" (its exception, named or at the slot, else the master), the
   * event itself when single.
   */
  function guardOf(
    s: Store,
    row: EventRow,
    scope: "this" | "following" | "all" | null | undefined,
    occ: number | null,
  ): EventRow {
    const master = row.recurrence_parent_id
      ? (eventOf(s, row.recurrence_parent_id) ?? null)
      : row.rrule
        ? row
        : null;
    if (!master) return row;
    if (scope === "following" || scope === "all") return master;
    if (row.recurrence_parent_id) return row;
    return occ !== null ? (exceptionAt(s, master.id, occ) ?? master) : master;
  }

  // Detail and occurrences ----------------------------------------------------------------------

  function detail(p: Principal, s: Store, row: EventRow, occ: number | null = null): Detail {
    const cal = calOf(s, row.calendar_id);
    const master = row.recurrence_parent_id
      ? eventOf(s, row.recurrence_parent_id)
      : row.rrule
        ? row
        : null;
    let starts = row.starts_at;
    let ends = row.ends_at;
    if (occ !== null && row.rrule) {
      const dur = ms(row.ends_at) - ms(row.starts_at);
      starts = iso(occ);
      ends = iso(occ + dur);
    }
    const me = meIdOf(p);
    const atts = attendeesOf(s, row.id);
    const mine = me ? atts.find((a) => a.membership_id === me) : undefined;
    return {
      ...row,
      starts_at: starts,
      ends_at: ends,
      calendar_name: cal?.name ?? "",
      calendar_kind: cal?.kind ?? "company",
      attendees: atts.map((a) => ({
        ...a,
        name: a.membership_id ? d.nameOf(s.company, a.membership_id) : a.email,
        member_kind: a.membership_id ? d.memberKindOf(s.company, a.membership_id) : null,
      })),
      organizer_name: row.organizer_membership_id
        ? d.nameOf(s.company, row.organizer_membership_id)
        : null,
      series_rrule: master?.rrule ?? null,
      series_id: master?.id ?? null,
      series_version: master?.version ?? null,
      occurrence_start: row.recurrence_parent_id
        ? row.original_start_at
        : occ !== null && row.rrule
          ? iso(occ)
          : null,
      my_response: mine?.response ?? null,
      can_edit: canEdit(p, s, row),
      can_rsvp: !!mine && row.organizer_membership_id !== me,
      read_only_reason: row.source === "meeting" ? "Edited through the meeting" : null,
      revisions: s.revisions.filter((r) => r.event_id === row.id).length,
    };
  }

  function occurrence(
    p: Principal,
    s: Store,
    row: EventRow,
    master: EventRow | null,
    start: number,
    mode: Mode,
    attending: boolean,
  ): Occurrence {
    const cal = calOf(s, row.calendar_id) as CalRow;
    const busy = mode === "busy";
    const dur = ms(row.ends_at) - ms(row.starts_at);
    const slot = master
      ? row.recurrence_parent_id && row.original_start_at
        ? ms(row.original_start_at)
        : start
      : null;
    const me = meIdOf(p);
    const atts = attendeesOf(s, row.id);
    const mine = me ? atts.find((a) => a.membership_id === me) : undefined;
    return {
      key: master && slot !== null ? `${master.id}:${iso(slot)}` : row.id,
      event_id: row.id,
      series_id: master?.id ?? null,
      calendar_id: row.calendar_id,
      starts_at: iso(start),
      ends_at: iso(start + dur),
      all_day: row.all_day,
      timezone: row.timezone,
      original_start_at: slot !== null ? iso(slot) : null,
      recurring: !!master,
      busy,
      title: busy ? null : row.title,
      location: busy ? null : row.location,
      status: row.status,
      source: row.source,
      source_ref: row.source_ref,
      organizer_membership_id: busy ? null : row.organizer_membership_id,
      organizer_name:
        busy || !row.organizer_membership_id
          ? null
          : d.nameOf(s.company, row.organizer_membership_id),
      owner_name: ownerNameOf(s, cal),
      attendee_count: busy ? null : atts.length,
      my_response: busy ? null : (mine?.response ?? null),
      attending,
      can_edit: !busy && canEdit(p, s, row),
      can_rsvp: !busy && !!mine && row.organizer_membership_id !== me,
    };
  }

  function windowOf(
    p: Principal,
    s: Store,
    from: number,
    to: number,
    ids: Set<string>,
    attendingOn: boolean,
  ) {
    const me = meIdOf(p);
    const out: Occurrence[] = [];
    const overlaps = (a: number, b: number) => a < to && b > from;
    for (const master of s.events) {
      if (master.recurrence_parent_id || master.status === "cancelled" || master.cancelled_at)
        continue;
      const inSet = ids.has(master.calendar_id);
      const attends =
        !!me &&
        (master.organizer_membership_id === me ||
          attendeesOf(s, master.id).some((a) => a.membership_id === me));
      if (!inSet && !(attendingOn && attends)) continue;
      const mode = modeOf(p, s, master);
      if (mode === "none") continue;
      const attending = !inSet;
      const rule = master.rrule ? parseRule(master.rrule) : null;
      const start = ms(master.starts_at);
      const dur = ms(master.ends_at) - start;
      if (!rule) {
        if (overlaps(start, start + dur))
          out.push(occurrence(p, s, master, null, start, mode, attending));
        continue;
      }
      const exceptions = exceptionsOf(s, master.id);
      const taken = new Set(exceptions.map((e) => ms(e.original_start_at ?? e.starts_at)));
      const until = master.series_until ? ms(master.series_until) : null;
      for (const slot of slotsOf(start, rule, until)) {
        if (slot >= to) break;
        if (taken.has(slot) || !overlaps(slot, slot + dur)) continue;
        out.push(occurrence(p, s, master, master, slot, mode, attending));
      }
      for (const e of exceptions) {
        if (e.status === "cancelled" || e.cancelled_at) continue;
        const es = ms(e.starts_at);
        if (!overlaps(es, ms(e.ends_at))) continue;
        const em = modeOf(p, s, e);
        if (em !== "none") out.push(occurrence(p, s, e, master, es, em, attending));
      }
    }
    out.sort((a, b) => ms(a.starts_at) - ms(b.starts_at) || a.key.localeCompare(b.key));
    return out;
  }

  // Seed ----------------------------------------------------------------------------------------

  /**
   * Acme's week (M = Monday 00:00 UTC of now's week; the visual tests pin now at Monday
   * 2026-10-19 10:00Z): an All-hands series, a webinar, an all-day offsite, a design review the
   * owner can answer, a lunch the owner declined, the owner's investor call, the member's focus
   * time (a busy block to the owner, who follows that calendar) and a team meeting projected from
   * the Meetings twin.
   */
  function seedAcme(s: Store) {
    const { owner, member } = d.seedPeople(s.company.id);
    if (!owner || !member) return;
    const M = mondayOf(d.now());
    const at = (days: number, h = 0, m = 0) => iso(M + days * DAY + h * HOUR + m * MIN);
    const company = s.calendars.find((c) => c.kind === "company") as CalRow;
    const ownerCal = personCal(s, owner);
    const memberCal = personCal(s, member);
    const teams = d.teamsOf(s.company);
    const ownerTeams = teamsHeld(s.company, owner);
    const team = teams.find((t) => ownerTeams.has(t.id)) ?? teams[0] ?? null;
    const teamCal = team ? s.calendars.find((c) => c.team_id === team.id) : undefined;
    const ev = (
      cal: CalRow,
      title: string,
      starts: string,
      ends: string,
      organizer: string,
      extra: Partial<EventRow> = {},
    ) =>
      newEvent(s, null, {
        calendar_id: cal.id,
        title,
        starts_at: starts,
        ends_at: ends,
        organizer_membership_id: organizer,
        actor_membership_id: organizer,
        ...extra,
      });
    const allHands = ev(company, "All-hands", at(-14, 15), at(-14, 16), owner, {
      rrule: "FREQ=WEEKLY;BYDAY=MO",
      location: "Main room",
      description: "The week ahead, wins and asks.",
    });
    addAttendee(s, allHands.id, { membership_id: owner }, "accepted");
    addAttendee(s, allHands.id, { membership_id: member });
    const webinar = ev(company, "Q4 webinar", at(2, 14), at(2, 15), owner, {
      location: "Zoom",
    });
    addAttendee(s, webinar.id, { membership_id: member });
    ev(company, "Company offsite", at(3), at(4), owner, { all_day: true, location: "Lisbon" });
    const review = ev(company, "Design review", at(4, 13), at(4, 14), member);
    addAttendee(s, review.id, { membership_id: owner });
    const lunch = ev(company, "Lunch & learn", at(0, 12), at(0, 13), member, {
      location: "Kitchen",
    });
    addAttendee(s, lunch.id, { membership_id: owner }, "declined");
    ev(ownerCal, "Investor call", at(1, 9), at(1, 9, 30), owner);
    ev(memberCal, "Focus time", at(1, 11), at(1, 12), member);
    if (teamCal)
      ev(teamCal, "Sales weekly", at(3, 10), at(3, 11), owner, {
        source: "meeting",
        source_ref: uid(0xcb0e, 1),
      });
    // The owner overlays the member's calendar (busy blocks) and, when not in it, the team's.
    const follow = (cal: CalRow) =>
      s.follows.push({
        membership_id: owner,
        calendar_id: cal.id,
        shown: true,
        color_override: null,
      });
    follow(memberCal);
    if (teamCal && !(team && ownerTeams.has(team.id))) follow(teamCal);
    // CAL-WORK-LAYERS: the owner published the Social media board's "Publish dates" view and
    // shared it with the member, who films the videos.
    const w = d.work?.(s.company.id) ?? null;
    const social = w?.boards.find((b) => b.name === "Social media");
    const publish = social
      ? w?.views?.find((v) => v.board_id === social.id && v.kind === "calendar")
      : undefined;
    if (publish) {
      const cal = calendarRow(s, "work", "Social calendar", {
        view_id: publish.id,
        owner_membership_id: owner,
        color: "rose",
        visibility: "details",
        actor_membership_id: owner,
      });
      const t = stamp();
      s.shares.push({
        company_id: s.company.id,
        id: newId(s, 3),
        calendar_id: cal.id,
        grantee_kind: "membership",
        grantee_id: member,
        level: "read",
        revoked_at: null,
        actor_kind: "person",
        actor_membership_id: owner,
        created_at: t,
        updated_at: t,
      });
    }
  }

  // Tasks overlay -------------------------------------------------------------------------------

  function chipsOf(p: Principal, s: Store, from: number, to: number): Chip[] {
    const now = d.now().getTime();
    const work = d.work?.(s.company.id) ?? null;
    if (!work) return [];
    const me = meIdOf(p);
    const held = heldBy(s.company, me);
    const boardName = (id: string) => work.boards.find((b) => b.id === id)?.name ?? null;
    const chip = (t: CalendarWorkTask, due: number): Chip => ({
      id: t.id,
      title: t.title,
      due_at: iso(due),
      board_id: t.board_id,
      board_name: boardName(t.board_id),
      done: t.completed_at !== null,
      overdue: t.completed_at === null && due < now,
    });
    // Only stored rows: the tasks on the caller's seats and on the boards it starred, due in the
    // window (the API's rule). Nothing is invented for a demo account.
    const starred = new Set(me ? (d.starredBoardsOf?.(p, me) ?? []) : []);
    const chips = work.tasks
      .filter(
        (t) =>
          t.due_at !== null &&
          ms(t.due_at) >= from &&
          ms(t.due_at) < to &&
          (t.assignee_seat_ids.some((x) => held.has(x)) || starred.has(t.board_id)),
      )
      .map((t) => chip(t, ms(t.due_at as string)));
    return chips.sort((a, b) => ms(a.due_at) - ms(b.due_at) || a.id.localeCompare(b.id));
  }

  // Layers (CAL-WORK-LAYERS) -------------------------------------------------------------------

  const dayText = (t: number) => iso(t).slice(0, 10);
  /** The last day of a quarter key (`2026-Q4` → 2026-12-31), or null. */
  function quarterEnd(key: string | null): string | null {
    const m = key ? /^(\d{4})-Q([1-4])$/.exec(key) : null;
    if (!m) return null;
    const end = Date.UTC(Number(m[1]), Number(m[2]) * 3, 0);
    return dayText(end);
  }
  function itemsOf(p: Principal, s: Store, cals: CalRow[], from: number, to: number): Item[] {
    const lo = dayText(from - DAY);
    const hi = dayText(to + DAY);
    const inDay = (day: string) => day >= lo && day <= hi;
    const out: Item[] = [];
    const me = meIdOf(p);
    const held = heldBy(s.company, me);
    for (const cal of cals) {
      if (cal.kind === "work") {
        const wk = workOf(s, cal);
        if (!wk) continue;
        const ref = wk.view.date_field ?? "due_at";
        const fieldId = ref.startsWith("field:") ? ref.slice(6) : null;
        const viaBoard = seesBoard(p, wk.space);
        const edits = editsBoard(p, wk.space);
        for (const t of wk.w.tasks) {
          if (t.board_id !== wk.board.id || t.deleted) continue;
          if (!viaBoard && t.restricted) continue;
          let at: string | null = null;
          let day: string | null = null;
          if (fieldId) {
            const v = t.fields?.[fieldId];
            day = typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
          } else {
            at = ref === "start_at" ? (t.start_at ?? null) : t.due_at;
            day = at ? at.slice(0, 10) : null;
          }
          if (!day || !inDay(day)) continue;
          const st = wk.board.status_set?.find((x) => x.key === t.status);
          out.push({
            key: `${cal.id}:${t.id}`,
            calendar_id: cal.id,
            kind: "task",
            ref_id: t.id,
            title: t.title,
            day,
            at,
            done: t.completed_at !== null,
            can_edit: !isAgent(p) && (edits || t.assignee_seat_ids.some((x) => held.has(x))),
            board_id: viaBoard ? wk.board.id : null,
            date_field: ref,
            version: t.version ?? 1,
            status_label: st?.name ?? null,
            status_color: st?.color ?? null,
            seat_title: null,
            milestone_kind: null,
          });
        }
      } else if (cal.kind === "milestones") {
        for (const g of d.goalsOf?.(s.company) ?? []) {
          if (g.state === "dropped") continue;
          const seat = d.seatTitleOf?.(s.company, g.seat_id) ?? null;
          const kind = g.metric_def_id && g.target !== null ? "target" : g.kind;
          const goalDay = g.due_at
            ? g.due_at.slice(0, 10)
            : g.kind === "priority"
              ? quarterEnd(g.period_key)
              : null;
          const base = {
            calendar_id: cal.id,
            kind: "milestone" as const,
            ref_id: g.id,
            at: null,
            can_edit: false,
            board_id: null,
            date_field: null,
            version: null,
            status_label: null,
            status_color: null,
            seat_title: seat,
          };
          if (goalDay && inDay(goalDay))
            out.push({
              ...base,
              key: `${cal.id}:${g.id}`,
              title: g.title,
              day: goalDay,
              done: g.state === "achieved",
              milestone_kind: kind as Item["milestone_kind"],
            });
          for (const m of g.milestones) {
            const md = m.due_at?.slice(0, 10) ?? null;
            if (!md || !inDay(md)) continue;
            out.push({
              ...base,
              key: `${cal.id}:${m.id}`,
              title: m.title,
              day: md,
              done: m.done_at !== null,
              milestone_kind: "milestone",
            });
          }
        }
      }
    }
    return out.sort((a, b) => a.day.localeCompare(b.day) || a.key.localeCompare(b.key));
  }

  // ICS -----------------------------------------------------------------------------------------

  const icsTime = (s: string) => untilText(ms(s));
  const icsText = (v: string) => v.replace(/[\\;,]/g, (c) => `\\${c}`).replace(/\n/g, "\\n");
  function icsOf(s: Store, membershipId: string): string {
    const own = personCal(s, membershipId);
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//CompanyOS//Calendar//EN",
      `X-WR-CALNAME:${icsText(s.company.name)}`,
    ];
    for (const e of s.events) {
      if (e.recurrence_parent_id || e.status === "cancelled") continue;
      const mine =
        e.calendar_id === own.id ||
        attendeesOf(s, e.id).some((a) => a.membership_id === membershipId) ||
        e.organizer_membership_id === membershipId;
      if (!mine) continue;
      lines.push(
        "BEGIN:VEVENT",
        `UID:${e.id}@companyos`,
        `DTSTAMP:${icsTime(e.updated_at)}`,
        `DTSTART:${icsTime(e.starts_at)}`,
        `DTEND:${icsTime(e.ends_at)}`,
        `SUMMARY:${icsText(e.title)}`,
        ...(e.location ? [`LOCATION:${icsText(e.location)}`] : []),
        ...(e.rrule ? [`RRULE:${e.rrule}`] : []),
        "END:VEVENT",
      );
    }
    lines.push("END:VCALENDAR");
    return `${lines.join("\r\n")}\r\n`;
  }

  // Handlers ------------------------------------------------------------------------------------

  const query = (url: URL) => Object.fromEntries(url.searchParams.entries());

  function visibleEvent(p: Principal, s: Store, id: string) {
    const row = eventOf(s, id);
    if (!row) return { error: notFound() } as const;
    const mode = modeOf(p, s, row);
    if (mode === "none") return { error: notFound() } as const;
    return { row, mode } as const;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "calendars.list": ({ principal: p }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const me = meOf(p);
      const own =
        me && me.kind === "person" && !clientOnly(p) ? personCal(s, me.membership_id) : null;
      return json({
        data: listed(p, s).map((cal) => view(p, s, cal)),
        my_calendar_id: own?.id ?? null,
        my_membership_id: me?.membership_id ?? null,
        timezone: zoneOf(p.company),
        week_start: weekStartOf(p.company),
      });
    },
    "calendars.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      if (!managesOf(p, s, cal)) return forbidden("only the calendar's managers change it");
      const r = CalendarPatch.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const b = r.data;
      if (b.name !== undefined) cal.name = b.name;
      if (b.color !== undefined) cal.color = b.color;
      if (b.visibility !== undefined) cal.visibility = b.visibility;
      if (b.timezone !== undefined) cal.timezone = b.timezone;
      cal.updated_at = stamp();
      return json(view(p, s, cal));
    },
    // CAL-WORK-LAYERS: a board's calendar view into the Calendar (board editors; idempotent).
    "calendars.publish": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarPublish.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const me = meIdOf(p);
      if (!me || isAgent(p)) return forbidden("a person publishes a work calendar");
      const w = d.work?.(s.company.id) ?? null;
      const v = w?.views?.find((x) => x.id === r.data.view_id) ?? null;
      const board = v ? w?.boards.find((b) => b.id === v.board_id && !b.archived_at) : null;
      if (!v || !board) return notFound();
      const space = w?.spaces?.find((x) => x.id === board.space_id) ?? null;
      if (!seesBoard(p, space)) return notFound();
      if (!editsBoard(p, space)) return forbidden("only the board's editors publish its views");
      if (v.kind !== "calendar") return validation("view_id: only a calendar view is published");
      const live = s.calendars.find(
        (c) => c.kind === "work" && c.view_id === v.id && !c.archived_at,
      );
      if (live) return json(view(p, s, live), 201);
      const cal = calendarRow(s, "work", r.data.name ?? board.name, {
        view_id: v.id,
        owner_membership_id: me,
        color: r.data.color ?? null,
        visibility: "details",
        actor_membership_id: me,
      });
      return json(view(p, s, cal), 201);
    },
    "calendars.unpublish": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal || cal.archived_at || (cal.kind === "work" && !inAudience(p, s, cal)))
        return notFound();
      if (cal.kind !== "work") return conflict("only a work calendar is taken out of the Calendar");
      const wk = workOf(s, cal);
      if (!managesOf(p, s, cal) && !(wk && editsBoard(p, wk.space)))
        return forbidden("its owner, an admin or a board editor takes it out");
      cal.archived_at = stamp();
      cal.updated_at = cal.archived_at;
      return noContent();
    },
    "calendarItems.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarWindowQuery.safeParse(query(url));
      if (!r.success) return validation(firstIssue(r.error));
      const from = ms(r.data.from);
      const to = ms(r.data.to);
      if (!(to > from)) return validation("to: must be after from");
      if (to - from > 100 * DAY) return validation("to: a window is at most 100 days");
      const me = meIdOf(p);
      const asked = r.data.calendar_ids
        ? new Set(
            r.data.calendar_ids
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          )
        : null;
      const cals = listed(p, s).filter(
        (cal) =>
          (cal.kind === "work" || cal.kind === "milestones") &&
          (asked ? asked.has(cal.id) : (followOf(s, me, cal.id)?.shown ?? true)),
      );
      const data = itemsOf(p, s, cals, from, to);
      return json({ data: data.slice(0, 2000), truncated: data.length > 2000 });
    },
    "calendarShares.list": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      if (!managesOf(p, s, cal)) return forbidden("only the calendar's managers see its shares");
      return json({
        data: s.shares
          .filter((x) => x.calendar_id === cal.id && !x.revoked_at)
          .map((x) => shareView(s, x)),
      });
    },
    "calendarShares.create": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      if (!managesOf(p, s, cal)) return forbidden("only the calendar's managers share it");
      const r = CalendarShareCreate.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const b = r.data;
      const grantee = b.grantee_kind === "company" ? s.company.id : b.grantee_id;
      if (!grantee) return validation("grantee_id: required for a member, a team or a seat");
      if (cal.kind === "work" && b.level !== "read")
        return validation("level: a work calendar is shared to read");
      const t = stamp();
      const live = s.shares.find(
        (x) =>
          x.calendar_id === cal.id &&
          !x.revoked_at &&
          x.grantee_kind === b.grantee_kind &&
          x.grantee_id === grantee,
      );
      if (live) {
        live.level = b.level;
        live.updated_at = t;
        return json(shareView(s, live), 201);
      }
      const share: Share = {
        company_id: s.company.id,
        id: newId(s, 3),
        calendar_id: cal.id,
        grantee_kind: b.grantee_kind,
        grantee_id: grantee,
        level: b.level,
        revoked_at: null,
        actor_kind: "person",
        actor_membership_id: meIdOf(p),
        created_at: t,
        updated_at: t,
      };
      s.shares.push(share);
      return json(shareView(s, share), 201);
    },
    "calendarShares.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      if (!managesOf(p, s, cal)) return forbidden("only the calendar's managers revoke shares");
      const share = s.shares.find(
        (x) => x.id === params.share && x.calendar_id === cal.id && !x.revoked_at,
      );
      if (!share) return notFound();
      share.revoked_at = stamp();
      share.updated_at = share.revoked_at;
      return noContent();
    },
    "calendarFollows.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarFollowCreate.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const b = r.data;
      if (!b.calendar_id === !b.membership_id)
        return validation("calendar_id: give a calendar or a membership, not both");
      const me = meIdOf(p);
      if (!me) return forbidden("no membership to follow with");
      let cal: CalRow | null = null;
      if (b.membership_id) {
        const known =
          d.assignmentsOf(s.company).some((a) => a.membership_id === b.membership_id) ||
          d.nameOf(s.company, b.membership_id) !== "Member";
        if (!known) return notFound();
        cal = personCal(s, b.membership_id);
      } else cal = calOf(s, b.calendar_id ?? "");
      if (!cal) return notFound();
      if (clientOnly(p) && cal.kind !== "company") return forbidden();
      let f = followOf(s, me, cal.id);
      if (!f) {
        f = { membership_id: me, calendar_id: cal.id, shown: true, color_override: null };
        s.follows.push(f);
      }
      if (b.shown !== undefined) f.shown = b.shown;
      if (b.color !== undefined) f.color_override = b.color;
      return json(view(p, s, cal));
    },
    "calendarFollows.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      const r = CalendarFollowPatch.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const me = meIdOf(p);
      if (!me) return forbidden("no membership to follow with");
      let f = followOf(s, me, cal.id);
      if (!f) {
        f = { membership_id: me, calendar_id: cal.id, shown: true, color_override: null };
        s.follows.push(f);
      }
      if (r.data.shown !== undefined) f.shown = r.data.shown;
      if (r.data.color !== undefined) f.color_override = r.data.color;
      return json(view(p, s, cal));
    },
    "calendarFollows.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const cal = calOf(s, params.id ?? "");
      if (!cal) return notFound();
      if (isDefault(p, s, cal))
        return conflict("the company's, your teams' and your own calendar can only be hidden");
      const me = meIdOf(p);
      s.follows = s.follows.filter((f) => !(f.membership_id === me && f.calendar_id === cal.id));
      return noContent();
    },
    "calendarEvents.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarWindowQuery.safeParse(query(url));
      if (!r.success) return validation(firstIssue(r.error));
      const from = ms(r.data.from);
      const to = ms(r.data.to);
      if (!(to > from)) return validation("to: must be after from");
      if (to - from > 100 * DAY) return validation("to: a window is at most 100 days");
      const me = meIdOf(p);
      const ids = new Set(
        r.data.calendar_ids
          ? r.data.calendar_ids
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : listed(p, s)
              .filter((cal) => followOf(s, me, cal.id)?.shown ?? true)
              .map((cal) => cal.id),
      );
      const data = windowOf(p, s, from, to, ids, r.data.attending !== "false");
      return json({
        data: data.slice(0, 2000),
        from: iso(from),
        to: iso(to),
        truncated: data.length > 2000,
      });
    },
    "calendarEvents.get": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const v = visibleEvent(p, s, params.id ?? "");
      if ("error" in v) return v.error;
      if (v.mode === "busy") return forbidden("you see this event as busy only");
      const occRaw = url.searchParams.get("occurrence_start");
      const occ = occRaw && !Number.isNaN(ms(occRaw)) ? ms(occRaw) : null;
      if (occRaw && occ === null) return validation("occurrence_start: not a timestamp");
      if (occ !== null && v.row.rrule) {
        const exc = exceptionAt(s, v.row.id, occ);
        if (exc) return json(detail(p, s, exc));
      }
      return json(detail(p, s, v.row, occ));
    },
    "calendarEvents.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarEventCreate.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const b = r.data;
      const cal = calOf(s, b.calendar_id);
      if (!cal) return notFound();
      if (accessOf(p, s, cal) !== "write") return forbidden("you cannot write to this calendar");
      if (ms(b.ends_at) < ms(b.starts_at)) return validation("ends_at: before starts_at");
      if (b.rrule && !parseRule(b.rrule)) return validation("rrule: unsupported rule");
      const row = newEvent(s, p, {
        calendar_id: cal.id,
        title: b.title,
        description: b.description ?? null,
        location: b.location ?? null,
        starts_at: iso(ms(b.starts_at)),
        ends_at: iso(ms(b.ends_at)),
        all_day: b.all_day ?? false,
        timezone: b.timezone ?? cal.timezone ?? zoneOf(s.company),
        status: b.status ?? "confirmed",
        visibility: b.visibility ?? "default",
        rrule: b.rrule ?? null,
      });
      if (b.attendees) setAttendees(s, row.id, b.attendees);
      return json(detail(p, s, row), 201);
    },
    "calendarEvents.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarEventPatch.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const b = r.data;
      const v = visibleEvent(p, s, params.id ?? "");
      if ("error" in v) return v.error;
      const row = v.row;
      if (row.source === "meeting") return forbidden("Edited through the meeting");
      if (!canEdit(p, s, row)) return forbidden("you cannot edit this event");
      if (
        b.version !== undefined &&
        b.version !==
          guardOf(s, row, b.scope, b.occurrence_start ? ms(b.occurrence_start) : null).version
      )
        return conflict("the event changed since you read it");
      if (b.calendar_id && b.calendar_id !== row.calendar_id) {
        const target = calOf(s, b.calendar_id);
        if (!target) return notFound();
        if (accessOf(p, s, target) !== "write")
          return forbidden("you cannot write to that calendar");
      }
      if (b.rrule && !parseRule(b.rrule)) return validation("rrule: unsupported rule");
      let target = row;
      let occ = b.occurrence_start ? ms(b.occurrence_start) : null;
      if (row.recurrence_parent_id && (b.scope === "following" || b.scope === "all")) {
        const master = eventOf(s, row.recurrence_parent_id);
        if (master) {
          target = master;
          occ = row.original_start_at ? ms(row.original_start_at) : occ;
        }
      }
      const fields = (e: EventRow) => {
        if (b.calendar_id !== undefined) e.calendar_id = b.calendar_id;
        if (b.title !== undefined) e.title = b.title;
        if (b.description !== undefined) e.description = b.description;
        if (b.location !== undefined) e.location = b.location;
        if (b.all_day !== undefined) e.all_day = b.all_day;
        if (b.timezone !== undefined) e.timezone = b.timezone;
        if (b.status !== undefined) e.status = b.status;
        if (b.visibility !== undefined) e.visibility = b.visibility;
        if (b.attendees !== undefined) setAttendees(s, e.id, b.attendees);
      };
      /** New times for an occurrence that started at `start` and lasted `dur`. */
      const times = (start: number, dur: number) => {
        const ns = b.starts_at ? ms(b.starts_at) : start;
        const ne = b.ends_at ? ms(b.ends_at) : ns + dur;
        return [ns, ne] as const;
      };
      const ordered = (a: number, z: number) => z >= a;
      if (!target.rrule) {
        const [ns, ne] = times(ms(target.starts_at), ms(target.ends_at) - ms(target.starts_at));
        if (!ordered(ns, ne)) return validation("ends_at: before starts_at");
        revise(s, p, target);
        fields(target);
        target.starts_at = iso(ns);
        target.ends_at = iso(ne);
        if (b.rrule !== undefined) target.rrule = b.rrule;
        bump(target);
        return json(detail(p, s, target));
      }
      const master = target;
      if (!b.scope)
        return validation("scope: required on a recurring event (this, following, all)");
      if (b.scope !== "all" && occ === null)
        return validation("occurrence_start: required for this scope");
      const mStart = ms(master.starts_at);
      const dur = ms(master.ends_at) - mStart;
      if (b.scope === "this" && occ !== null) {
        const existing = exceptionAt(s, master.id, occ);
        const exc = existing ?? newException(s, p, master, occ);
        const [ns, ne] = times(ms(exc.starts_at), ms(exc.ends_at) - ms(exc.starts_at));
        if (!ordered(ns, ne)) return validation("ends_at: before starts_at");
        if (existing) revise(s, p, exc);
        fields(exc);
        exc.starts_at = iso(ns);
        exc.ends_at = iso(ne);
        if (existing) bump(exc);
        return json(detail(p, s, exc));
      }
      if (b.scope === "following" && occ !== null && occ > mStart) {
        const rule = parseRule(master.rrule as string);
        const [ns, ne] = times(occ, dur);
        if (!ordered(ns, ne)) return validation("ends_at: before starts_at");
        let before = 0;
        if (rule)
          for (const slot of slotsOf(mStart, rule, null))
            if (slot < occ) before++;
            else break;
        const origUntil = master.series_until;
        const origRule = master.rrule as string;
        revise(s, p, master);
        master.series_until = iso(occ - 1000);
        master.rrule = ruleWith(origRule, occ - 1000, null);
        bump(master);
        const { id: _id, ...rest } = master;
        const t = stamp();
        const next = newEvent(s, p, {
          ...rest,
          starts_at: iso(ns),
          ends_at: iso(ne),
          series_until: origUntil,
          rrule:
            b.rrule !== undefined
              ? b.rrule
              : ruleWith(
                  origRule,
                  rule?.until ?? null,
                  rule?.count ? Math.max(1, rule.count - before) : null,
                ),
          version: 1,
          created_at: t,
          updated_at: t,
        });
        copyAttendees(s, master.id, next.id);
        fields(next);
        for (const e of exceptionsOf(s, master.id))
          if (e.original_start_at && ms(e.original_start_at) >= occ)
            e.recurrence_parent_id = next.id;
        return json(detail(p, s, next));
      }
      // `all` (or `following` from the first occurrence): the master, its times shifted by the
      // same offset as the occurrence edited.
      const base = occ ?? mStart;
      const ns = b.starts_at ? ms(b.starts_at) : base;
      const ne = b.ends_at ? ms(b.ends_at) : ns + dur;
      if (!ordered(ns, ne)) return validation("ends_at: before starts_at");
      revise(s, p, master);
      fields(master);
      master.starts_at = iso(mStart + (ns - base));
      master.ends_at = iso(mStart + (ns - base) + (ne - ns));
      if (b.rrule !== undefined) master.rrule = b.rrule;
      bump(master);
      return json(detail(p, s, master));
    },
    "calendarEvents.delete": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const v = visibleEvent(p, s, params.id ?? "");
      if ("error" in v) return v.error;
      const row = v.row;
      if (row.source === "meeting") return forbidden("Edited through the meeting");
      if (!canEdit(p, s, row)) return forbidden("you cannot cancel this event");
      const scope = url.searchParams.get("scope");
      if (scope !== null && scope !== "this" && scope !== "following" && scope !== "all")
        return validation("scope: this, following or all");
      const occRaw = url.searchParams.get("occurrence_start");
      let occ = occRaw ? ms(occRaw) : null;
      if (occRaw && Number.isNaN(occ)) return validation("occurrence_start: not a timestamp");
      const versionRaw = url.searchParams.get("version");
      if (versionRaw !== null) {
        const version = Number(versionRaw);
        if (!Number.isInteger(version) || version < 1)
          return validation("version: a whole number from 1");
        const guard = guardOf(s, row, scope as "this" | "following" | "all" | null, occ);
        if (guard.version !== version) return conflict("the event changed since you read it");
      }
      const t = stamp();
      const cancel = (e: EventRow) => {
        e.status = "cancelled";
        e.cancelled_at = t;
        e.version += 1;
        e.updated_at = t;
      };
      let target = row;
      if (row.recurrence_parent_id) {
        const master = eventOf(s, row.recurrence_parent_id);
        if (master && (scope === "following" || scope === "all")) {
          target = master;
          occ = row.original_start_at ? ms(row.original_start_at) : occ;
        } else {
          cancel(row);
          return noContent();
        }
      }
      if (!target.rrule || scope === null || scope === "all") {
        cancel(target);
        return noContent();
      }
      if (occ === null) return validation("occurrence_start: required for this scope");
      if (scope === "this") {
        cancel(exceptionAt(s, target.id, occ) ?? newException(s, p, target, occ));
        return noContent();
      }
      if (occ <= ms(target.starts_at)) cancel(target);
      else {
        target.series_until = iso(occ - 1000);
        target.rrule = ruleWith(target.rrule, occ - 1000, null);
        bump(target);
        for (const e of exceptionsOf(s, target.id))
          if (e.original_start_at && ms(e.original_start_at) >= occ) cancel(e);
      }
      return noContent();
    },
    "calendarEvents.rsvp": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarRsvp.safeParse(body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const v = visibleEvent(p, s, params.id ?? "");
      if ("error" in v) return v.error;
      if (v.mode === "busy") return forbidden("you see this event as busy only");
      const me = meIdOf(p);
      const row = v.row;
      const mine = attendeesOf(s, row.id).find((a) => a.membership_id === me);
      if (!me || !mine) return forbidden("you are not invited to this event");
      const t = stamp();
      const answer = (a: Attendee) => {
        a.response = r.data.response;
        a.responded_at = t;
        a.updated_at = t;
      };
      // Simplified: `this` on a series answers the series (the twin keeps no per-slot answers).
      answer(mine);
      const seriesId = row.rrule ? row.id : null;
      if (seriesId && r.data.scope !== "this")
        for (const e of exceptionsOf(s, seriesId))
          for (const a of attendeesOf(s, e.id)) if (a.membership_id === me) answer(a);
      const occ = r.data.occurrence_start ? ms(r.data.occurrence_start) : null;
      return json(detail(p, s, row, occ));
    },
    "calendarEvents.revisions": ({ principal: p, params }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const v = visibleEvent(p, s, params.id ?? "");
      if ("error" in v) return v.error;
      if (v.mode === "busy") return forbidden("you see this event as busy only");
      return json({
        data: s.revisions.filter((x) => x.event_id === v.row.id).reverse(),
      });
    },
    "calendarTasks.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarTaskQuery.safeParse(query(url));
      if (!r.success) return validation(firstIssue(r.error));
      const from = ms(r.data.from);
      const to = ms(r.data.to);
      if (!(to > from)) return validation("to: must be after from");
      return json({ data: chipsOf(p, s, from, to) });
    },
    "calendarChanges.create": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const s = storeOf(p.company);
      const r = CalendarChangeCreate.safeParse(ctx.body ?? {});
      if (!r.success) return validation(firstIssue(r.error));
      const change = r.data.change;
      const co = p.company.id;
      const pending = (eventId: string | null) =>
        json({ outcome: "pending", event_id: eventId, approval_id: newId(s, 6) });
      const call = (id: RouteId, c: Partial<Ctx>) => (handlers[id] as Handler)({ ...ctx, ...c });
      if (change.op === "create") {
        const cal = calOf(s, change.event.calendar_id);
        if (!cal) return notFound();
        if (accessOf(p, s, cal) !== "write") return pending(null);
        const res = call("calendarEvents.create", { body: change.event, params: { co } });
        const made = peekJson<Detail>(res);
        if (!made) return res;
        return json({ outcome: "applied", event_id: made.id, approval_id: null });
      }
      const row = eventOf(s, change.event_id);
      if (!row || modeOf(p, s, row) === "none") return notFound();
      if (!canEdit(p, s, row)) return pending(row.id);
      if (change.op === "update") {
        const res = call("calendarEvents.update", {
          body: change.patch,
          params: { co, id: row.id },
        });
        const done = peekJson<Detail>(res);
        if (!done) return res;
        return json({ outcome: "applied", event_id: done.id, approval_id: null });
      }
      const u = new URL(`http://fake.local/v1/companies/${co}/calendar/events/${row.id}`);
      if (change.scope) u.searchParams.set("scope", change.scope);
      if (change.occurrence_start) u.searchParams.set("occurrence_start", change.occurrence_start);
      const res = call("calendarEvents.delete", {
        url: u,
        params: { co, id: row.id },
        body: undefined,
      });
      if (res.status !== 204) return res;
      return json({ outcome: "applied", event_id: row.id, approval_id: null });
    },
    "calendarFeed.get": ({ principal: p }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const me = meIdOf(p);
      const f = me ? s.feeds.get(me) : undefined;
      return json({
        active: !!f,
        created_at: f?.created_at ?? null,
        last_used_at: f?.last_used_at ?? null,
      });
    },
    "calendarFeed.create": ({ principal: p }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const me = meIdOf(p);
      if (!me) return forbidden("no membership to make a feed for");
      const token = `cf${globalThis.crypto.randomUUID().replace(/-/g, "")}`;
      const f: Feed = { token, created_at: stamp(), last_used_at: null };
      s.feeds.set(me, f);
      return json(
        {
          token,
          path: `/v1/companies/${p.company.id}/calendar/feed/${token}`,
          created_at: f.created_at,
        },
        201,
      );
    },
    "calendarFeed.delete": ({ principal: p }) => {
      if (!p) return notFound();
      const s = storeOf(p.company);
      const me = meIdOf(p);
      if (me) s.feeds.delete(me);
      return noContent();
    },
    // No principal: the token in the path is the credential.
    "calendarFeed.ics": ({ params }) => {
      const s = stores.get(params.co ?? "");
      if (!s) return notFound();
      for (const [membershipId, f] of s.feeds)
        if (f.token === params.token) {
          f.last_used_at = stamp();
          return new Response(icsOf(s, membershipId), {
            status: 200,
            headers: { "content-type": "text/calendar; charset=utf-8" },
          });
        }
      return notFound();
    },
  };

  function shareView(s: Store, x: Share): ShareView {
    const grantee_name =
      x.grantee_kind === "company"
        ? s.company.name
        : x.grantee_kind === "team"
          ? (teamOf(s.company, x.grantee_id)?.name ?? null)
          : x.grantee_kind === "seat"
            ? (d.seatTitleOf?.(s.company, x.grantee_id) ?? null)
            : d.nameOf(s.company, x.grantee_id);
    return { ...x, grantee_name };
  }

  return handlers;
}
